/*
 * Merino.tech promo engine (Oct 2026). One file drives three things on the storefront:
 *   1. the ladder plaque on product pages  - containers .mtw[data-mt-wknd] printed by snippets/mt-qty-ladder
 *   2. the ladder discount codes           - applied by themselves; a foreign code in the cart is never replaced.
 *                                            Only ONE ladder code is kept in the cart: a ladder code from another tier
 *                                            is removed (via /cart/update.js), so it can't take over in checkout and
 *                                            push the free gift out of its discount.
 *                                            LADDER MODE (the metafield says "ladder_code": true): ALL the step codes of the
 *                                            active document (HALLOWEEN15 + HALLOWEEN20) are kept in the cart together and
 *                                            never swapped. Shopify applies the best one that qualifies, so the cart and
 *                                            checkout always name the step's own code (20% shows as HALLOWEEN20), and
 *                                            crossing a step needs no write: the cart renders once. When the first step
 *                                            serves ANY order the set goes into the cart right before the theme's first
 *                                            «Add to cart» (that answer already carries the discount: one render, nothing
 *                                            written on page load); with a minimum it is attached when that step is reached.
 *                                            Without the key every step sends its own code (swapped per step).
 *   3. the free-gift pop-up                - one gift per order. The gift line carries two line properties:
 *                                            "Gift" (label the shopper sees) and "_mt_gift" (campaign id, hidden, used
 *                                            to recognise the line here and in order analytics)
 *
 * SETTINGS are not written here. They come from window.MT_PROMO, which snippets/mt-promo-config prints from the
 * shop metafield mt_promo.config (the dashboard writes it). When the metafield is missing or broken, the
 * built-in window.MT_PROMO_DEFAULT from the same snippet is used.
 *
 * A document may carry "override": a complete seasonal document with its own starts_at / ends_at. While the clock
 * is inside that window the override is the active document (even when the base says enabled:false); the choice
 * is made again on every cart read, so a page left open switches by itself when the window opens or closes.
 * window.MT_PROMO_SOURCE tells which one runs: "metafield" (base), "override" (seasonal) or "default". The codes
 * of the other document of the pair count as ladder codes too, so a code left over from it is replaced.
 *
 * Other optional keys: "skin" (class mtw--<skin> on each plaque box and mtg--<skin> on the pop-up; the CSS lives in
 * the theme), a FIRST tier with "min": 0 = "any order" (printed as "<lbl> <show>", e.g. "Any order"; on an empty
 * cart it is not reached and its CTA is plaque.empty_cta), "lbl" on any tier (replaces plaque.from_label there)
 * and gift.popup.cta (the button label when the gift product has a single variant).
 *
 * Thresholds ("min", in cents) are compared with the cart BEFORE the discount; the gift line and gift cards do not count.
 * In one-code mode a coded step is shown as reached on the base the Function counts (lines before ANY discount).
 * The gift becomes $0 through a Shopify automatic discount (Buy X get Y). If the gift line is ever not free
 * (cart dropped under the threshold, a discount that does not combine) it is removed, so nobody pays for a gift.
 *
 * Funnel events for analytics are published as Shopify.analytics.publish('mt_promo:<name>', data).
 */
(function () {
  'use strict';
  if (window.__mtPromo) return;
  window.__mtPromo = true;

  var nativeFetch = window.fetch.bind(window);
  var JSON_HEAD = { 'Content-Type': 'application/json', Accept: 'application/json' };
  var SEL = '.mtw[data-mt-wknd]';
  var GIFT_LABEL = 'Gift';      // visible line property
  var GIFT_MARK = '_mt_gift';   // hidden line property = campaign id
  var SKIP_KEY = 'mt_gift_skip';

  /* ------------------------------------------------------------------ settings */
  function txt(v, fallback) { return v == null || v === '' ? (fallback || '') : String(v); }
  function when(v) { var t = v ? Date.parse(v) : NaN; return isNaN(t) ? 0 : t; }
  function parsed(raw) {
    var c = raw;
    if (typeof c === 'string') { try { c = JSON.parse(c); } catch (e) { c = null; } }
    return c && typeof c === 'object' && !Array.isArray(c) ? c : null;
  }
  function codesOf(doc) {
    var out = [];
    if (!doc || typeof doc !== 'object') return out;
    (Array.isArray(doc.tiers) ? doc.tiers : []).forEach(function (t) { if (t && t.code) out.push(txt(t.code).trim().toUpperCase()); });
    (Array.isArray(doc.retired_codes) ? doc.retired_codes : []).forEach(function (x) { out.push(txt(x).trim().toUpperCase()); });
    return out;
  }
  // `other` = the other document of the pair (the base for an override and vice versa): only its codes are used.
  // `one` = the metafield's "ladder_code": true (one-code mode, see the top of the file).
  function normalize(raw, other, one) {
    var c = parsed(raw);
    if (!c || !Array.isArray(c.tiers)) return null;
    var tiers = c.tiers.map(function (t) {
      t = t || {};
      var min = Math.round(Number(t.min));
      var pct = Number(t.percent) || 0;
      return {
        min: min,
        code: txt(t.code).trim().toUpperCase(),
        pct: pct,
        gift: !!t.gift,
        show: txt(t.show, min > 0 ? '$' + Math.round(min / 100) : 'order'),
        lbl: txt(t.lbl, min > 0 ? '' : 'Any'),
        title: txt(t.title),
        note: txt(t.note),
        short: txt(t.short, pct ? 'takes ' + pct + '% off' : 'adds a free gift')
      };
    }).filter(function (t) { return t.min >= 0 && (t.code || t.gift); })
      .sort(function (a, b) { return a.min - b.min; })
      .filter(function (t, i) { return t.min > 0 || i === 0; })   // "any order" (min 0) only as the first step
      .slice(0, 4);

    var g = c.gift || {};
    var p = g.popup || {};
    var giftMin = 0;
    tiers.forEach(function (t) { if (t.gift && !giftMin) giftMin = t.min; });
    var options = (Array.isArray(g.options) ? g.options : []).map(function (o) {
      o = o || {};
      return {
        handle: txt(o.handle).trim(),
        name: txt(o.name),
        ids: (Array.isArray(o.variant_ids) ? o.variant_ids : []).map(String),
        sizes: (Array.isArray(o.sizes) ? o.sizes : []).map(String)
      };
    }).filter(function (o) { return o.handle; }).slice(0, 4);
    var giftIds = [];
    options.forEach(function (o) { giftIds = giftIds.concat(o.ids); });

    var ladder = [];
    tiers.forEach(function (t) { if (t.code) ladder.push(t.code); });
    (Array.isArray(c.retired_codes) ? c.retired_codes : []).forEach(function (x) { ladder.push(txt(x).trim().toUpperCase()); });
    ladder = ladder.concat(codesOf(other)).filter(function (x, i, a) { return x && a.indexOf(x) === i; });

    var skin = txt(c.skin).trim().toLowerCase();
    if (!/^[a-z0-9-]{1,24}$/.test(skin)) skin = '';
    var pl = c.plaque || {};
    return {
      enabled: c.enabled !== false && tiers.length > 0,
      one: one === true,
      campaign: txt(c.campaign, 'ladder'),
      skin: skin,
      starts: when(c.starts_at),
      ends: when(c.ends_at),
      currency: txt(c.currency).toUpperCase(),
      tiers: tiers,
      ladder: ladder,
      plaque: { eyebrow: txt(pl.eyebrow), title: txt(pl.title), from: txt(pl.from_label, 'From'), intro: txt(pl.intro), emptyCta: txt(pl.empty_cta, 'Add an item') },
      gift: {
        enabled: g.enabled !== false && giftMin > 0 && options.length > 0,
        min: giftMin,
        label: txt(g.label, 'Free gift'),
        short: txt(g.short, 'a free gift'),
        pick: txt(g.cta_pick, 'Pick gift'),
        added: txt(g.cta_added, 'Gift added'),
        options: options,
        ids: giftIds,
        eyebrow: txt(p.eyebrow, 'Free gift unlocked'),
        title: txt(p.title, 'Pick your free gift'),
        text: txt(p.text),
        decline: txt(p.decline, 'No thanks'),
        cta: txt(p.cta, 'Add free gift')
      }
    };
  }

  // The active document: the override while the clock is inside its window, else the base; the theme's default
  // when the metafield is missing or broken (a broken override falls back to its base).
  function pick() {
    var c = parsed(window.MT_PROMO);
    if (c) {
      var o = c.override && typeof c.override === 'object' && !Array.isArray(c.override) ? c.override : null;
      var one = c.ladder_code === true;   // set on the metafield itself, for the base and the override alike
      var now = Date.now();
      var s = o ? when(o.starts_at) : 0;
      var e = o ? when(o.ends_at) : 0;
      if (o && s && e && now >= s && now < e) { var ov = normalize(o, c, one); if (ov) return { cfg: ov, source: 'override' }; }
      var base = normalize(c, o, one);
      if (base) return { cfg: base, source: 'metafield' };
    }
    var d = normalize(window.MT_PROMO_DEFAULT);
    return d ? { cfg: d, source: 'default' } : null;
  }
  var picked = pick();
  if (!picked) return;
  var CFG = picked.cfg;
  var SOURCE = picked.source;
  window.MT_PROMO_SOURCE = SOURCE;
  var LADDER = ladderOf(CFG);        // ladder mode: the first step with a code; recomputed when the active document switches
  var LADDER_CODE = LADDER ? LADDER.code : '';   // "" = per-step mode: every step sends its own code
  var LADDER_MIN = LADDER ? LADDER.min : 0;
  var LADDER_SET = setOf(CFG);       // ladder mode: EVERY step code of the document, all kept in the cart at once

  function isOn() {
    if (!CFG.enabled) return false;
    var now = Date.now();
    if (CFG.starts && now < CFG.starts) return false;
    if (CFG.ends && now >= CFG.ends) return false;
    var cur = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (CFG.currency && cur && String(cur).toUpperCase() !== CFG.currency) return false;
    return true;
  }

  /* ------------------------------------------------------------------ helpers */
  var state = { base: 0, fn: 0, count: 0, codes: [], all: [], hasGift: false, ready: false, known: false };
  var inFlight = '';     // ladder code being applied right now
  var writing = null;    // that write (a promise): the theme's own cart writes wait for it
  var attempt = '';      // "CODE|cart" we already tried, so a code that does not apply is not sent in a loop
  var giftBusy = false;
  var viewed = false;

  function esc(s) { return String(s).replace(/[&<>"']/g, function (ch) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]; }); }
  function up(c) { return '$' + Math.ceil(c / 100); }
  function rd(c) { return '$' + Math.round(c / 100); }
  function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }
  function el(tag, cls, text) { var n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; }
  function track(name, data) {
    try {
      var d = data || {};
      d.campaign = CFG.campaign;
      d.source = SOURCE;
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') window.Shopify.analytics.publish('mt_promo:' + name, d);
    } catch (e) {}
  }
  function refreshDrawer() {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
    if (document.querySelector('cart-drawer')) return;
    var form = document.querySelector('cart-form');   // cart page without the drawer
    if (form && typeof form.refresh === 'function') { try { form.refresh(); } catch (e) {} }
  }
  function cartJson() { return nativeFetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(function (r) { return r.json(); }); }
  function post(url, body) { return nativeFetch(url, { method: 'POST', credentials: 'same-origin', headers: JSON_HEAD, body: JSON.stringify(body) }); }
  function skipped() { try { return !!sessionStorage.getItem(SKIP_KEY); } catch (e) { return false; } }
  function setSkip() { try { sessionStorage.setItem(SKIP_KEY, '1'); } catch (e) {} }

  function read(cart) {
    var sub = (cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price) || 0;
    var gifts = [];
    var auto = false;
    var fn = 0;        // the base the discount Function counts: the lines BEFORE any discount (its cost.subtotalAmount)
    var loose = null;  // the first gift-variant line WITHOUT the gift mark (added outside the pop-up)
    (cart.items || []).forEach(function (it) {
      if (it.properties && it.properties[GIFT_MARK]) { gifts.push(it); sub -= (it.final_line_price || 0); }
      else if (it.gift_card) sub -= (it.final_line_price || 0);
      else {
        fn += it.original_line_price != null ? it.original_line_price : (it.final_line_price || 0);
        if (CFG.gift.ids.indexOf(String(it.variant_id)) !== -1) {
          if (!loose) loose = it;
          if ((it.final_line_price || 0) < (it.original_line_price || 0)) auto = true;
        }
      }
    });
    // as the Function does: without a marked gift line, ONE unit of a loose gift is left out once the rest reaches the gift step
    if (!gifts.length && loose && CFG.gift.enabled) {
      var q = Math.max(1, loose.quantity || 1);
      var unit = loose.original_price != null ? loose.original_price : Math.round((loose.original_line_price != null ? loose.original_line_price : (loose.final_line_price || 0)) / q);
      if (unit > 0 && fn - unit >= CFG.gift.min) fn -= unit;
    }
    var codes = [];
    var all = [];
    (cart.discount_codes || []).forEach(function (d) {
      if (!d || !d.code) return;
      var c = String(d.code).toUpperCase();
      all.push(c);
      if (d.applicable !== false) codes.push(c);
    });
    return { base: Math.max(0, sub), fn: Math.max(0, fn), gifts: gifts, auto: auto, codes: codes, all: all, count: cart.item_count || 0 };
  }
  // a step is reached from its threshold; the "any order" step (min 0) needs at least one item, never an empty cart
  function reached(t, base) { return t.min > 0 ? base >= t.min : base > 0; }
  // one-code mode only: the FIRST step that has a code names the ladder code; it serves every step (the Function
  // computes the step). null without "ladder_code" in the metafield: then every step sends its own code
  function ladderOf(cfg) { var s = null; if (cfg.one) cfg.tiers.forEach(function (t) { if (!s && t.code) s = t; }); return s; }
  // ladder mode: all the step codes of the document (HALLOWEEN15 + HALLOWEEN20). They go into the cart together and
  // Shopify applies the best one that qualifies (measured on the live store 2026-10-22: $129.98 → HALLOWEEN15 15%,
  // $259.96 → HALLOWEEN20 20%, whatever the order). So the cart and checkout always name the step the shopper is on,
  // and crossing a step needs no write: the cart renders once.
  function setOf(cfg) { var out = []; if (cfg.one) cfg.tiers.forEach(function (t) { if (t.code && out.indexOf(t.code) === -1) out.push(t.code); }); return out; }
  function missingFrom(all) { return LADDER_SET.some(function (c) { return all.indexOf(c) === -1; }); }
  // the base a step is measured on (`s` = state or a cart read): a coded step served by the ladder code is the
  // Function's call, so its base; every other step (gift steps, per-step codes) the engine's own base, as always
  function baseFor(t, s) { return LADDER_CODE && t.code ? s.fn : s.base; }
  // one-code mode: the ladder code once its first step is reached; per step: the code of the highest step reached
  function wantCode(s) {
    if (LADDER_CODE) return reached({ min: LADDER_MIN }, s.fn) ? LADDER_SET.join(',') : '';
    var c = ''; CFG.tiers.forEach(function (t) { if (t.code && reached(t, s.base)) c = t.code; }); return c;
  }
  function current(s) { var i = -1; CFG.tiers.forEach(function (t, n) { if (reached(t, baseFor(t, s))) i = n; }); return i; }
  function fillPct(s) { var t = CFG.tiers[CFG.tiers.length - 1]; var base = baseFor(t, s); var top = t.min; return top > 0 ? Math.min(100, Math.round(base / top * 100)) : (base > 0 ? 100 : 0); }

  /* ------------------------------------------------------------------ plaque */
  function build(box) {
    if (box.__mtBuilt) return;
    box.__mtBuilt = true;
    box.textContent = '';
    box.setAttribute('data-mt-source', SOURCE);
    if (box.__mtSkin) box.classList.remove(box.__mtSkin);
    box.__mtSkin = CFG.skin ? 'mtw--' + CFG.skin : '';
    if (box.__mtSkin) box.classList.add(box.__mtSkin);
    var top = el('div', 'mtw__top');
    if (CFG.plaque.eyebrow) top.appendChild(el('span', 'mtw__eyebrow', CFG.plaque.eyebrow));
    if (CFG.plaque.title) top.appendChild(el('span', 'mtw__title', CFG.plaque.title));
    if (top.childNodes.length) box.appendChild(top);

    var steps = el('div', 'mtw__steps');
    CFG.tiers.forEach(function (t, i) {
      var b = el('button', 'mtw__step');
      b.type = 'button';
      b.setAttribute('data-mt-tier', String(i));
      var pct = el('span', 'mtw__pct');
      pct.appendChild(el('span', 'mtw__lbl', t.lbl || CFG.plaque.from));
      pct.appendChild(document.createTextNode(t.show));
      var copy = el('span', 'mtw__copy');
      copy.appendChild(el('span', 'mtw__val', t.title));
      if (t.note) copy.appendChild(el('span', 'mtw__note', t.note));
      b.appendChild(pct); b.appendChild(copy); b.appendChild(el('span', 'mtw__cta', t.show));
      steps.appendChild(b);
    });
    box.appendChild(steps);

    var trackBar = el('div', 'mtw__track');
    trackBar.appendChild(el('span', 'mtw__fill'));
    var topMin = CFG.tiers[CFG.tiers.length - 1].min;
    CFG.tiers.slice(0, -1).forEach(function (t) {
      if (t.min <= 0) return;   // the "any order" step has no mark on the bar
      var tick = el('span', 'mtw__tick');
      tick.style.left = Math.round(t.min / topMin * 100) + '%';
      trackBar.appendChild(tick);
    });
    box.appendChild(trackBar);
    var msg = el('p', 'mtw__msg');
    msg.setAttribute('aria-live', 'polite');
    box.appendChild(msg);
  }

  function message(s) {
    var T = CFG.tiers;
    var base = s.base;
    var cur = current(s);
    var next = T[cur + 1];
    if (base === 0) {
      return CFG.plaque.intro ? esc(CFG.plaque.intro) : T.map(function (t) { return '<b>' + esc(t.min > 0 ? t.show : t.lbl + ' ' + t.show) + '</b> ' + esc(t.short); }).join(', ') + '.';
    }
    var giftOn = CFG.gift.enabled && base >= CFG.gift.min;
    var head = '';
    if (cur >= 0) {
      var t = T[cur];
      if (t.pct) head = 'You save <b>' + rd(base * t.pct / 100) + '</b>' + (giftOn ? ' and get ' + esc(CFG.gift.short) : '') + '. ';
      else if (giftOn) head = esc(cap(CFG.gift.short)) + ' unlocked. ';
    }
    if (next) return head + '<b>' + up(next.min - baseFor(next, s)) + ' more</b> ' + esc(next.short) + '.';
    return head + (T[cur].pct && T.length > 1 ? 'Best rate on the ladder.' : '');
  }

  function paint(box) {
    if (!isOn()) { box.hidden = true; return; }
    build(box);
    var T = CFG.tiers;
    var cur = current(state);
    box.querySelectorAll('[data-mt-tier]').forEach(function (b) {
      var i = Number(b.getAttribute('data-mt-tier'));
      var t = T[i];
      var base = baseFor(t, state);
      var on = reached(t, base);
      var code = t.code;   // ladder mode too: Shopify marks applicable exactly the code of the step it applied
      var applied = code && state.codes.indexOf(code) !== -1;
      var label;
      if (!on) label = t.min > 0 ? up(t.min - base) + ' to go' : CFG.plaque.emptyCta;
      else if (!t.code) label = state.hasGift ? CFG.gift.added : CFG.gift.pick;
      else if (i < cur) label = 'Passed';
      else label = inFlight === code ? 'Applying' : (applied ? 'Applied' : 'Apply');
      b.querySelector('.mtw__cta').textContent = label;
      b.classList.toggle('is-on', i === cur);
      b.classList.toggle('is-done', i === cur && (t.code ? applied : state.hasGift));
      b.classList.toggle('is-dim', on && i < cur);
      if (on) b.removeAttribute('aria-disabled'); else b.setAttribute('aria-disabled', 'true');
    });
    box.querySelector('.mtw__fill').style.width = fillPct(state) + '%';
    box.querySelector('.mtw__msg').innerHTML = message(state);
    box.hidden = false;
    if (!viewed) { viewed = true; track('plaque_view', { source: SOURCE }); }
  }
  function paintAll() { if (state.ready) document.querySelectorAll(SEL).forEach(paint); }

  /* ------------------------------------------------------------------ ladder codes */
  function isLadder(c) { return CFG.ladder.indexOf(c) !== -1; }
  // `w` is one code, or in ladder mode the comma list of the whole set: any other ladder code in the cart is stale
  function staleLadder(all, w) { var keep = String(w).split(','); return all.some(function (c) { return isLadder(c) && keep.indexOf(c) === -1; }); }
  // a code a cart already holds never lets ours in next to it (on an empty cart none applies yet: then any counts)
  function foreignIn(s) { return (s.count ? s.codes : s.all).some(function (c) { return !isLadder(c); }); }
  // `quiet`: the code goes in right before the theme's own «Add to cart», whose answer renders the drawer. Otherwise
  // the drawer is redrawn after the write unless the cart the update answered with is empty (nothing to redraw).
  // Returns the write; the theme's cart writes wait for it (see gate).
  function sendCode(code, quiet) {
    inFlight = code;
    paintAll();
    // the full set of codes the cart should end up with: codes that are not ours stay, other ladder codes go
    var keep = state.all.filter(function (c) { return !isLadder(c); });
    String(code).split(',').forEach(function (c) { if (c && keep.indexOf(c) === -1) keep.push(c); });
    writing = post('/cart/update.js', { discount: keep.join(',') })
      .then(function (r) {
        track('code_sent', { code: code });
        if (!r || !r.ok) return null;
        state.all = keep;   // what the cart holds now, until the next read says more
        return typeof r.json === 'function' ? r.json().then(null, function () { return null; }) : null;
      }, function () { return null; })
      .then(function (cart) {
        inFlight = ''; writing = null;
        if (!quiet && !(cart && cart.item_count === 0)) refreshDrawer();
        schedule(150);
      });
    return writing;
  }
  function codeStep(info) {
    var w = wantCode(info);
    // one-code mode: the ladder code counts once it is in the cart, applicable or not; per step: once it applies
    var has = LADDER_CODE ? !!w && !missingFrom(info.all) : info.codes.indexOf(w) !== -1;
    // an empty cart gets no code here: the ladder code goes in with the theme's first «Add to cart» (attachFirst)
    if (!w || foreignIn(info) || !info.count || (has && !staleLadder(info.all, w))) { attempt = ''; return false; }
    var key = w + '|' + info.base;
    if (attempt === key) return false;
    attempt = key;
    sendCode(w);
    return true;
  }
  // One-code mode with a ladder code for ANY order: it is attached right BEFORE the theme's «Add to cart» while the
  // cart lacks it (or holds another ladder code), so the add's own answer already carries the discount and the drawer
  // renders once. Only on a cart the engine has read (state.known): a code it cannot see must never be replaced.
  function attachWanted() {
    if (!LADDER_CODE || LADDER_MIN > 0 || inFlight || !state.known || !isOn() || foreignIn(state)) return false;
    return missingFrom(state.all) || staleLadder(state.all, LADDER_SET.join(','));
  }
  function attachFirst() {
    return Promise.resolve(state.known ? null : refresh()).then(function () {
      if (writing) return writing;   // the read itself sent the codes (a cart with lines): the add waits for that write
      if (!attachWanted()) return null;
      attempt = LADDER_SET.join(',') + '|' + state.base;
      return sendCode(LADDER_SET.join(','), true);
    });
  }
  // what a theme cart write must wait for (null = nothing, it goes at once): the ladder code going in first (an add),
  // or the engine's own code write in flight — never two cart writes at once. At most 4 s, whatever happens.
  function gate(url) {
    var p = url.indexOf('/cart/add') !== -1 && LADDER_CODE && !LADDER_MIN && (!state.known || attachWanted()) ? attachFirst() : writing;
    if (!p) return null;
    return new Promise(function (done) {
      var t = setTimeout(done, 4000);
      p.then(function () { clearTimeout(t); done(); }, function () { clearTimeout(t); done(); });
    });
  }

  /* ------------------------------------------------------------------ free gift */
  function giftDone() { giftBusy = false; refreshDrawer(); schedule(60); }
  function removeLine(line) { return post('/cart/change.js', { id: line.key, quantity: 0 }); }
  function giftStep(info, manual) {
    if (giftBusy) return;
    var G = CFG.gift;
    var line = info.gifts[0];
    if (info.gifts.length > 1) { giftBusy = true; removeLine(info.gifts[1]).then(giftDone, giftDone); return; }
    if (line && line.quantity > 1) { giftBusy = true; post('/cart/change.js', { id: line.key, quantity: 1 }).then(giftDone, giftDone); return; }
    var eligible = isOn() && G.enabled && info.base >= G.min;
    if (line && (!eligible || line.final_line_price > 0)) {
      if (eligible) setSkip();
      giftBusy = true;
      track('gift_removed', { reason: eligible ? 'not_free' : 'below_threshold' });
      removeLine(line).then(giftDone, giftDone);
      return;
    }
    if (!line && !info.auto && eligible && (manual === true || !skipped())) openGift();
  }

  function allowed(o, v) {
    if (o.ids.length) return o.ids.indexOf(String(v.id)) !== -1;
    if (o.sizes.length) return o.sizes.indexOf(String(v.title)) !== -1;
    return true;
  }
  function variantLabel(p, v) {
    if (v.title === 'Default Title') return CFG.gift.cta;
    var names = (p.options || []).map(function (o) { return typeof o === 'string' ? o : (o && o.name) || ''; });
    return names.length === 1 && names[0] ? names[0] + ' ' + v.title : v.title;
  }
  function openGift() {
    if (document.querySelector('.mtg') || openGift.loading) return;
    var G = CFG.gift;
    openGift.loading = true;
    Promise.all(G.options.map(function (o) {
      return nativeFetch('/products/' + encodeURIComponent(o.handle) + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
    })).then(function (products) {
      openGift.loading = false;
      var rows = [];
      products.forEach(function (p, i) {
        if (!p) return;
        var vs = (p.variants || []).filter(function (v) { return v.available && allowed(G.options[i], v); });
        if (vs.length) rows.push({ p: p, o: G.options[i], vs: vs });
      });
      if (!rows.length || document.querySelector('.mtg')) return;
      showGift(rows);
    }, function () { openGift.loading = false; });
  }

  function showGift(rows) {
    var G = CFG.gift;
    var multi = rows.length > 1;
    var opener = document.activeElement;
    var m = el('div', 'mtg' + (multi ? ' mtg--multi' : '') + (CFG.skin ? ' mtg--' + CFG.skin : ''));
    var box = el('div', 'mtg__box');
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-labelledby', 'mtg-t');
    var x = el('button', 'mtg__x', '×');
    x.type = 'button'; x.setAttribute('data-close', ''); x.setAttribute('aria-label', 'Close');
    box.appendChild(x);
    if (!multi && rows[0].p.featured_image) {
      var img = el('img', 'mtg__img');
      img.src = rows[0].p.featured_image; img.alt = ''; img.width = 120; img.height = 120;
      box.appendChild(img);
    }
    box.appendChild(el('p', 'mtg__eyebrow', G.eyebrow));
    var h = el('h2', 'mtg__title', G.title);
    h.id = 'mtg-t';
    box.appendChild(h);
    if (G.text) box.appendChild(el('p', 'mtg__text', G.text));

    function sizeButtons(row) {
      var wrap = el('div', 'mtg__sizes');
      row.vs.forEach(function (v) {
        var b = el('button', 'mtg__size', variantLabel(row.p, v));
        b.type = 'button';
        b.setAttribute('data-variant', String(v.id));
        if (multi) b.setAttribute('aria-label', (row.o.name || row.p.title) + ', ' + variantLabel(row.p, v));
        wrap.appendChild(b);
      });
      return wrap;
    }
    if (!multi) box.appendChild(sizeButtons(rows[0]));
    else {
      var list = el('div', 'mtg__rows');
      rows.forEach(function (row) {
        var r = el('div', 'mtg__row');
        if (row.p.featured_image) {
          var t = el('img', 'mtg__thumb');
          t.src = row.p.featured_image; t.alt = ''; t.width = 64; t.height = 64;
          r.appendChild(t);
        }
        var main = el('div', 'mtg__rowmain');
        main.appendChild(el('p', 'mtg__name', row.o.name || row.p.title));
        main.appendChild(sizeButtons(row));
        r.appendChild(main);
        list.appendChild(r);
      });
      box.appendChild(list);
    }
    var err = el('p', 'mtg__err');
    err.setAttribute('role', 'alert'); err.hidden = true;
    box.appendChild(err);
    var no = el('button', 'mtg__skip', G.decline);
    no.type = 'button'; no.setAttribute('data-close', '');
    box.appendChild(no);
    m.appendChild(box);
    document.body.appendChild(m);
    document.documentElement.classList.add('mtg-open');
    m.querySelector('.mtg__size').focus();
    track('gift_popup_open', { options: rows.length });

    function close(skip) {
      if (skip) { setSkip(); track('gift_declined', {}); }
      document.removeEventListener('keydown', onKey, true);
      document.documentElement.classList.remove('mtg-open');
      m.remove();
      if (opener && opener.focus) { try { opener.focus(); } catch (e) {} }
    }
    function onKey(e) {
      if (e.key === 'Escape') { e.stopPropagation(); close(true); return; }
      if (e.key !== 'Tab') return;
      var f = m.querySelectorAll('button:not([disabled])');
      var first = f[0];
      var last = f[f.length - 1];
      if (!m.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    function fail(btn, label, text) { giftBusy = false; btn.textContent = label; err.textContent = text; err.hidden = false; }
    document.addEventListener('keydown', onKey, true);
    m.addEventListener('click', function (e) {
      if (e.target === m || e.target.closest('[data-close]')) { close(true); return; }
      var btn = e.target.closest('.mtg__size');
      if (!btn || giftBusy) return;
      var label = btn.textContent;
      var variant = btn.getAttribute('data-variant');
      giftBusy = true;
      btn.textContent = 'Adding...';
      var props = {};
      props[GIFT_LABEL] = G.label;
      props[GIFT_MARK] = CFG.campaign;
      post('/cart/add.js', { items: [{ id: Number(variant), quantity: 1, properties: props }] })
        .then(function (r) { if (!r.ok) throw new Error('add'); return cartJson(); })
        .then(function (cart) {
          var line = read(cart).gifts[0];
          if (line && line.final_line_price > 0) {
            setSkip();
            track('gift_removed', { reason: 'not_free' });
            return removeLine(line).then(function () {
              fail(btn, label, 'This gift can’t be combined with another discount in your cart.');
              refreshDrawer(); schedule(60);
            });
          }
          track('gift_added', { variant_id: variant });
          close(false);
          giftDone();
          return null;
        })
        .catch(function () { fail(btn, label, 'Could not add the gift. Please try again.'); });
    });
  }

  /* ------------------------------------------------------------------ cart loop */
  // Which document is active is decided again on every cart read: when the override's window has opened or
  // closed since the last read, the plaque is rebuilt from the other document and the codes are re-checked.
  function syncDoc() {
    var p = pick();
    if (!p || p.source === SOURCE) return;
    CFG = p.cfg;
    SOURCE = p.source;
    window.MT_PROMO_SOURCE = SOURCE;
    LADDER = ladderOf(CFG);
    LADDER_CODE = LADDER ? LADDER.code : '';
    LADDER_MIN = LADDER ? LADDER.min : 0;
    LADDER_SET = setOf(CFG);
    attempt = '';
    document.querySelectorAll(SEL).forEach(function (box) { box.__mtBuilt = false; });
  }
  function handle(cart, manual) {
    syncDoc();
    var info = read(cart);
    state.base = info.base;
    state.fn = info.fn;
    state.count = info.count;
    state.codes = info.codes;
    state.all = info.all;
    state.hasGift = info.gifts.length > 0 || info.auto;
    state.ready = true;
    state.known = true;
    paintAll();
    if (inFlight) return;
    if (isOn() && codeStep(info)) return;
    giftStep(info, manual);
  }
  var pending = null;
  function refresh(manual) {
    if (pending) return manual === true ? pending.then(function () { return refresh(true); }) : pending;
    pending = cartJson()
      .then(function (c) { handle(c, manual); })
      .catch(function () { state.ready = true; paintAll(); })
      .then(function () { pending = null; });
    return pending;
  }
  var timer = null;
  function schedule(ms) { clearTimeout(timer); timer = setTimeout(refresh, ms == null ? 100 : ms); }

  /* ------------------------------------------------------------------ styles of the pop-up */
  var style = document.createElement('style');
  style.textContent =
    'html.mtg-open{overflow:hidden}' +
    'html.mtg-open #meri-ai-chat-root .meri-chat-launcher,html.mtg-open #meri-ai-chat-root .meri-chat-nudge{visibility:hidden!important;opacity:0!important;pointer-events:none!important}' +
    '.mtg{position:fixed;inset:0;z-index:2147483600;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(22,35,46,.55)}.mtg *{box-sizing:border-box}' +
    '.mtg__box{position:relative;width:100%;max-width:400px;max-height:calc(100vh - 32px);max-height:calc(100dvh - 32px);overflow:auto;overscroll-behavior:contain;padding:26px 22px 20px;border-radius:12px;background:#fff;color:#16232E;text-align:center;box-shadow:0 24px 60px rgba(0,0,0,.3);font-size:14px;line-height:1.45}' +
    '.mtg--multi .mtg__box{max-width:460px}' +
    '.mtg .mtg__x{position:absolute;top:4px;right:4px;width:44px;height:44px;min-height:0;margin:0;padding:0;border:0;background:none;box-shadow:none;font-size:26px;line-height:1;color:#16232E;cursor:pointer}' +
    '.mtg__img{display:block;width:120px;height:120px;object-fit:cover;border-radius:8px;margin:0 auto 12px}' +
    '.mtg__eyebrow{margin:0 0 6px;font-size:10.5px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#27503A}' +
    '.mtg__title{margin:0 0 8px;padding:0 28px;font-size:20px;font-weight:700;line-height:1.2;color:#16232E;text-transform:none;letter-spacing:normal}.mtg__text{margin:0 0 16px;color:#586470}' +
    '.mtg__sizes{display:flex;gap:10px}.mtg .mtg__size{flex:1 1 0;min-width:0;min-height:52px;margin:0;padding:0 8px;border:0;border-radius:8px;background:#27503A;color:#fff;box-shadow:none;font:inherit;font-size:16px;font-weight:700;text-transform:none;letter-spacing:normal;cursor:pointer}' +
    '.mtg .mtg__size:hover{background:#1d3d2c;color:#fff}.mtg .mtg__size:focus-visible,.mtg .mtg__x:focus-visible,.mtg .mtg__skip:focus-visible{outline:2px solid #16232E;outline-offset:2px}' +
    '.mtg__rows{text-align:left}.mtg__row{display:flex;align-items:center;gap:12px;padding:12px 0;border-top:1px solid #E6E0D3}.mtg__thumb{flex:0 0 64px;width:64px;height:64px;object-fit:cover;border-radius:8px}' +
    '.mtg__rowmain{flex:1 1 auto;min-width:0}.mtg__name{margin:0 0 8px;font-size:14px;font-weight:700;line-height:1.25;color:#16232E}.mtg__row .mtg__sizes{flex-wrap:wrap;gap:8px}.mtg .mtg__row .mtg__size{flex:1 1 72px;min-height:44px;font-size:15px}' +
    '.mtg__err{margin:12px 0 0;color:#A93B2C;font-size:13px}.mtg__err[hidden]{display:none}.mtg .mtg__skip{margin:14px 0 0;min-height:32px;padding:0 8px;border:0;background:none;box-shadow:none;color:#586470;font:inherit;font-size:13px;text-transform:none;letter-spacing:normal;text-decoration:underline;cursor:pointer}' +
    '@media (max-width:480px){.mtg{align-items:flex-end;padding:0}.mtg__box,.mtg--multi .mtg__box{max-width:none;max-height:92vh;max-height:92dvh;border-radius:16px 16px 0 0;padding:24px 16px calc(16px + env(safe-area-inset-bottom))}.mtg__img{width:96px;height:96px}.mtg .mtg__size{min-height:56px;font-size:17px}.mtg .mtg__row .mtg__size{min-height:48px;font-size:16px}.mtg .mtg__skip{min-height:44px}}' +
    '@media (prefers-reduced-motion:no-preference){.mtg{animation:mtgFade .18s ease-out}.mtg__box{animation:mtgRise .22s ease-out}}@keyframes mtgFade{from{opacity:0}}@keyframes mtgRise{from{transform:translateY(14px);opacity:0}}';
  document.head.appendChild(style);

  /* ------------------------------------------------------------------ wiring */
  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('.mtw [data-mt-tier]');
    if (!b) return;
    e.preventDefault();
    var t = CFG.tiers[Number(b.getAttribute('data-mt-tier'))];
    if (!t || !reached(t, baseFor(t, state))) return;
    var w = wantCode(state);
    track('tier_click', { tier_min: t.min });
    var missing = LADDER_CODE ? missingFrom(state.all) : state.codes.indexOf(w) === -1;
    if (t.code && w && missing && inFlight !== w) { attempt = w + '|' + state.base; sendCode(w); return; }
    if (t.gift && CFG.gift.enabled && !state.hasGift && state.base >= CFG.gift.min) refresh(true);
  });
  document.addEventListener('mt:gift:open', function () { refresh(true); });

  function isWrite(u) {
    if (typeof u !== 'string') return false;
    return u.indexOf('/cart/add') !== -1 || u.indexOf('/cart/change') !== -1 || u.indexOf('/cart/update') !== -1 || u.indexOf('/cart/clear') !== -1;
  }
  window.fetch = function () {
    var args = arguments;
    var a = args[0];
    var url = typeof a === 'string' ? a : String((a && (a.url || a.href)) || '');
    var wait = isWrite(url) ? gate(url) : null;
    var res = wait ? wait.then(function () { return nativeFetch.apply(window, args); }) : nativeFetch.apply(window, args);
    if (isWrite(url)) res.then(function () { schedule(120); }, function () {});
    return res;
  };
  var xhrOpen = XMLHttpRequest.prototype.open;
  var xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url, async) {
    this.__mtWrite = isWrite(url) && async !== false ? url : '';   // a synchronous request cannot wait
    if (isWrite(url)) this.addEventListener('load', function () { schedule(120); });
    return xhrOpen.apply(this, arguments);
  };
  if (typeof xhrSend === 'function') {
    XMLHttpRequest.prototype.send = function () {
      var wait = this.__mtWrite ? gate(this.__mtWrite) : null;
      if (!wait) return xhrSend.apply(this, arguments);
      var go = Function.prototype.apply.bind(xhrSend, this, arguments);
      wait.then(function () { go(); });
    };
  }
  ['on:cart:add', 'cart:updated', 'cart:refresh', 'cart:change'].forEach(function (ev) {
    document.addEventListener(ev, function () { schedule(150); });
  });

  function start() { if (document.querySelector(SEL)) refresh(); else schedule(1200); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
